import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import DocumentCapture from '../../components/ui/DocumentCapture';

const ACCENT = '#60A5FA';

const ALBARAN_STATES = ['Recibido', 'Parcial', 'Pendiente Factura', 'Facturado', 'Cerrado'];
const STATE_COLORS = {
  'Recibido': '#60A5FA', 'Parcial': '#FBBF24', 'Pendiente Factura': '#F97316',
  'Facturado': '#A78BFA', 'Cerrado': '#34D399',
};

const KPIS = [
  { label: 'Albaranes Recibidos', value: '—', icon: 'FileCheck', color: '#60A5FA' },
  { label: 'Pendientes Factura', value: '—', icon: 'Clock', color: '#FBBF24' },
  { label: 'Discrepancias', value: '—', icon: 'AlertTriangle', color: '#F87171' },
  { label: 'Conciliados', value: '—', icon: 'CheckCircle', color: '#34D399' },
];

const MOCK_CONCILIATION = [
  {
    id: 1,
    albaran: 'ALB-2026-001',
    factura: 'FAC-2026-001',
    pedido: 'PED-2026-001',
    proveedor: 'Proveedor A',
    fecha: '2026-07-20',
    discrepancias: [
      { campo: 'Cantidad', albaran: '10 kg', factura: '12 kg', diferencia: '+2 kg', tipo: 'warning' },
      { campo: 'Precio', albaran: '15,00 €', factura: '15,50 €', diferencia: '+0,50 €', tipo: 'warning' },
    ],
    estado: 'Parcial',
  },
];

export default function FinAlbaranes() {
  const [showCapture, setShowCapture] = useState(false);
  const [albaranes, setAlbaranes] = useState([]);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('Todos');
  const [selectedAlbaran, setSelectedAlbaran] = useState(null);
  const [activeTab, setActiveTab] = useState('lista');

  const handleSaveAlbaran = (data) => {
    const newAlbaran = {
      id: Date.now(),
      numero: 'ALB-' + Date.now(),
      proveedor: data?.ocrData?.proveedor || '—',
      fecha: data?.ocrData?.fecha_factura || new Date()?.toISOString()?.split('T')?.[0],
      total: data?.ocrData?.total_factura || '—',
      estado: 'Recibido',
      factura: '—',
      pedido: '—',
      discrepancias: [],
    };
    setAlbaranes(prev => [newAlbaran, ...prev]);
    setShowCapture(false);
  };

  const allAlbaranes = [...MOCK_CONCILIATION, ...albaranes];
  const filtered = allAlbaranes?.filter(a => {
    const matchSearch = !search || a?.albaran?.toLowerCase()?.includes(search?.toLowerCase()) || a?.proveedor?.toLowerCase()?.includes(search?.toLowerCase());
    const matchState = stateFilter === 'Todos' || a?.estado === stateFilter;
    return matchSearch && matchState;
  });

  return (
    <div className="min-h-screen px-4 py-6 md:px-8" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5 font-oranienbaum text-white/30" style={{ fontSize: 'var(--fs-sm)' }}>
        <Icon name="Home" size={13} />
        <span>/</span>
        <span className="text-[#D6EF4E]/60">Finanzas Corporativas</span>
        <span>/</span>
        <span className="text-white/50">Albaranes</span>
      </div>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(96,165,250,0.10)', border: '1px solid rgba(96,165,250,0.25)' }}>
            <Icon name="FileCheck" size={22} style={{ color: ACCENT }} />
          </div>
          <div>
            <h1 className="font-oranienbaum font-bold text-white tracking-wide" style={{ fontSize: 'var(--fs-page-title)' }}>Albaranes</h1>
            <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-sm)' }}>Captura · OCR · Conciliación Albarán ↔ Factura ↔ Pedido</p>
          </div>
        </div>
        <button onClick={() => setShowCapture(true)}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-oranienbaum font-bold transition-all hover:opacity-90"
          style={{ background: ACCENT, color: '#000', fontSize: 'var(--fs-btn)' }}>
          <Icon name="Camera" size={16} /> 📷 Foto Albarán
        </button>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {KPIS?.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-5 flex flex-col gap-3" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', minHeight: '130px' }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}30` }}>
              <Icon name={kpi?.icon} size={17} style={{ color: kpi?.color }} />
            </div>
            <div>
              <p className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-kpi-value)' }}>{kpi?.value}</p>
              <p className="font-oranienbaum text-white/45 mt-0.5" style={{ fontSize: 'var(--fs-kpi-label)' }}>{kpi?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        {[
          { id: 'lista', label: 'Lista de Albaranes', icon: 'List' },
          { id: 'conciliacion', label: 'Conciliación 3 Vías', icon: 'GitMerge' },
          { id: 'discrepancias', label: 'Discrepancias', icon: 'AlertTriangle' },
        ]?.map(tab => (
          <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-oranienbaum transition-all"
            style={{
              background: activeTab === tab?.id ? `${ACCENT}15` : 'rgba(255,255,255,0.04)',
              border: `1px solid ${activeTab === tab?.id ? `${ACCENT}35` : 'rgba(255,255,255,0.08)'}`,
              color: activeTab === tab?.id ? ACCENT : 'rgba(255,255,255,0.55)',
              fontSize: 'var(--fs-sm)',
              fontWeight: activeTab === tab?.id ? 600 : 400,
            }}>
            <Icon name={tab?.icon} size={14} />
            {tab?.label}
          </button>
        ))}
      </div>
      {/* Lista tab */}
      {activeTab === 'lista' && (
        <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            <div className="flex items-center gap-2 flex-1 min-w-[200px] rounded-xl px-3 border" style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.10)', minHeight: 'var(--control-height-sm)' }}>
              <Icon name="Search" size={14} className="text-white/35" />
              <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Buscar albarán, proveedor..."
                className="flex-1 bg-transparent outline-none font-oranienbaum text-white placeholder-white/25"
                style={{ fontSize: 'var(--fs-base)' }} />
            </div>
            <select value={stateFilter} onChange={e => setStateFilter(e?.target?.value)}
              className="font-oranienbaum rounded-xl px-3 border outline-none text-white"
              style={{ background: '#0D0D0D', borderColor: 'rgba(255,255,255,0.10)', minHeight: 'var(--control-height-sm)', fontSize: 'var(--fs-base)' }}>
              <option value="Todos">Todos los estados</option>
              {ALBARAN_STATES?.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto">
            {filtered?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <Icon name="FileCheck" size={32} className="text-white/20" />
                <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-base)' }}>Sin albaranes. Usa "📷 Foto Albarán" para capturar.</p>
              </div>
            ) : (
              <table className="w-full font-oranienbaum">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                    {['Albarán', 'Factura', 'Pedido', 'Proveedor', 'Fecha', 'Discrepancias', 'Estado', 'Acciones']?.map(h => (
                      <th key={h} className="text-left px-4 py-3 text-white/40 font-medium" style={{ fontSize: 'var(--fs-table-header)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered?.map(alb => (
                    <tr key={alb?.id} className="hover:bg-white/3 transition-colors cursor-pointer" onClick={() => setSelectedAlbaran(alb)}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td className="px-4 py-3 text-white font-medium" style={{ fontSize: 'var(--fs-table-row)' }}>{alb?.albaran || alb?.numero}</td>
                      <td className="px-4 py-3 text-white/60" style={{ fontSize: 'var(--fs-table-row)' }}>{alb?.factura || '—'}</td>
                      <td className="px-4 py-3 text-white/60" style={{ fontSize: 'var(--fs-table-row)' }}>{alb?.pedido || '—'}</td>
                      <td className="px-4 py-3 text-white/70" style={{ fontSize: 'var(--fs-table-row)' }}>{alb?.proveedor}</td>
                      <td className="px-4 py-3 text-white/60" style={{ fontSize: 'var(--fs-table-row)' }}>{alb?.fecha}</td>
                      <td className="px-4 py-3">
                        {alb?.discrepancias?.length > 0 ? (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(248,113,113,0.12)', color: '#F87171', fontSize: 'var(--fs-badge)' }}>
                            <Icon name="AlertTriangle" size={11} /> {alb?.discrepancias?.length}
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full" style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399', fontSize: 'var(--fs-badge)' }}>OK</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-full font-medium" style={{ background: `${STATE_COLORS?.[alb?.estado] || '#fff'}15`, color: STATE_COLORS?.[alb?.estado] || '#fff', fontSize: 'var(--fs-badge)' }}>
                          {alb?.estado}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button className="w-8 h-8 rounded-lg flex items-center justify-center text-white/35 hover:text-white hover:bg-white/8 transition-all">
                          <Icon name="Eye" size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {/* Conciliación tab */}
      {activeTab === 'conciliacion' && (
        <div className="space-y-4">
          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <h3 className="font-oranienbaum font-bold text-white mb-4" style={{ fontSize: 'var(--fs-section-title)' }}>Conciliación 3 Vías: Albarán ↔ Factura ↔ Pedido</h3>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Albarán', icon: 'FileCheck', color: '#60A5FA', desc: 'Documento de entrega del proveedor' },
                { label: 'Factura', icon: 'FileText', color: '#D6EF4E', desc: 'Factura emitida por el proveedor' },
                { label: 'Pedido de Compra', icon: 'ShoppingCart', color: '#34D399', desc: 'Pedido generado internamente' },
              ]?.map((item, i) => (
                <div key={i} className="rounded-xl p-4 text-center" style={{ background: `${item?.color}08`, border: `1px solid ${item?.color}20` }}>
                  <Icon name={item?.icon} size={24} style={{ color: item?.color, margin: '0 auto 8px' }} />
                  <p className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-base)' }}>{item?.label}</p>
                  <p className="font-oranienbaum text-white/40 mt-1" style={{ fontSize: 'var(--fs-xs)' }}>{item?.desc}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.06)' }} />
              <span className="font-oranienbaum text-white/30 text-xs uppercase tracking-widest">Comparación automática</span>
              <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.06)' }} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {['Cantidades', 'Precios', 'Descuentos', 'IVA', 'Total Factura', 'Lotes y Caducidades']?.map(campo => (
                <div key={campo} className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <Icon name="GitCompare" size={14} className="text-white/35" />
                  <span className="font-oranienbaum text-white/60" style={{ fontSize: 'var(--fs-sm)' }}>{campo}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Discrepancias tab */}
      {activeTab === 'discrepancias' && (
        <div className="space-y-4">
          {MOCK_CONCILIATION?.filter(a => a?.discrepancias?.length > 0)?.map(alb => (
            <div key={alb?.id} className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(248,113,113,0.20)' }}>
              <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                <div className="flex items-center gap-3">
                  <Icon name="AlertTriangle" size={16} className="text-red-400" />
                  <span className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-base)' }}>{alb?.albaran} — {alb?.proveedor}</span>
                </div>
                <span className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-xs)' }}>{alb?.fecha}</span>
              </div>
              <div className="p-5">
                <div className="grid grid-cols-4 gap-2 mb-3 text-xs font-oranienbaum text-white/40 uppercase tracking-widest">
                  <span>Campo</span><span>Albarán</span><span>Factura</span><span>Diferencia</span>
                </div>
                {alb?.discrepancias?.map((d, i) => (
                  <div key={i} className="grid grid-cols-4 gap-2 py-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                    <span className="font-oranienbaum text-white/70" style={{ fontSize: 'var(--fs-sm)' }}>{d?.campo}</span>
                    <span className="font-oranienbaum text-white/60" style={{ fontSize: 'var(--fs-sm)' }}>{d?.albaran}</span>
                    <span className="font-oranienbaum text-white/60" style={{ fontSize: 'var(--fs-sm)' }}>{d?.factura}</span>
                    <span className="font-oranienbaum font-bold" style={{ color: '#F87171', fontSize: 'var(--fs-sm)' }}>{d?.diferencia}</span>
                  </div>
                ))}
                <div className="flex items-center gap-2 mt-4">
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs transition-all"
                    style={{ background: 'rgba(248,113,113,0.12)', color: '#F87171', border: '1px solid rgba(248,113,113,0.25)' }}>
                    <Icon name="Flag" size={12} /> Marcar incidencia
                  </button>
                  <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-oranienbaum text-xs transition-all"
                    style={{ background: 'rgba(52,211,153,0.12)', color: '#34D399', border: '1px solid rgba(52,211,153,0.25)' }}>
                    <Icon name="Check" size={12} /> Aceptar diferencia
                  </button>
                </div>
              </div>
            </div>
          ))}
          {MOCK_CONCILIATION?.filter(a => a?.discrepancias?.length > 0)?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Icon name="CheckCircle" size={32} className="text-green-400/50" />
              <p className="font-oranienbaum text-white/40" style={{ fontSize: 'var(--fs-base)' }}>Sin discrepancias detectadas</p>
            </div>
          )}
        </div>
      )}
      {/* Albaran detail modal */}
      {selectedAlbaran && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.80)' }}>
          <div className="w-full max-w-2xl rounded-2xl overflow-hidden" style={{ background: '#111', border: `1px solid ${ACCENT}25` }}>
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
              <h3 className="font-oranienbaum font-bold text-white" style={{ fontSize: 'var(--fs-section-title)' }}>Detalle Albarán: {selectedAlbaran?.albaran || selectedAlbaran?.numero}</h3>
              <button onClick={() => setSelectedAlbaran(null)} className="text-white/40 hover:text-white transition-colors">
                <Icon name="X" size={16} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Proveedor', value: selectedAlbaran?.proveedor },
                  { label: 'Fecha', value: selectedAlbaran?.fecha },
                  { label: 'Factura asociada', value: selectedAlbaran?.factura || '—' },
                  { label: 'Pedido asociado', value: selectedAlbaran?.pedido || '—' },
                  { label: 'Estado', value: selectedAlbaran?.estado },
                  { label: 'Discrepancias', value: selectedAlbaran?.discrepancias?.length || 0 },
                ]?.map((item, i) => (
                  <div key={i}>
                    <p className="font-oranienbaum text-white/40 text-xs mb-1">{item?.label}</p>
                    <p className="font-oranienbaum text-white font-medium" style={{ fontSize: 'var(--fs-base)' }}>{item?.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Capture modal */}
      {showCapture && (
        <DocumentCapture
          moduleLabel="Albarán"
          accentColor={ACCENT}
          onSave={handleSaveAlbaran}
          onClose={() => setShowCapture(false)}
        />
      )}
    </div>
  );
}
